import { Router, useHistory } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import { CssBaseline, StyledEngineProvider } from '@mui/material';
import * as SentryReact from '@sentry/react';
import * as Sentry from '@sentry/capacitor';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { history } from 'shared/history';
import { useOnboardingState } from 'state/useOnboardingState';
import { getDeviceInfo } from 'state/localStorageTyped';
import { useEffect, useRef } from 'react';
import { differenceInMinutes } from 'date-fns';
import { Capacitor } from '@capacitor/core';
import { useCapacitorListener } from 'hooks/useCapacitorListener';
import { useCacheState } from 'state/useCacheState';
import { useAuthState } from 'state/useAuthState';
import { KeyboardShortcutsDialog } from 'components/KeyboardShortcutsDialog';
import { CommandPaletteDialog } from 'components/CommandPaletteDialog';
import { ConfirmProvider } from 'components/ConfirmDialog';
import { WatchDutySnackbarProvider } from 'libs/notistack/WatchDutySnackbarProvider/WatchDutySnackbarProvider';
import { useClearMapSelectionsForRouterEntity } from 'features/mapping/hooks/useClearMapSelectionsForRouterEntity';
import { useArePushNotificationsEnabled } from 'features/notifications/hooks/useNotificationsSettings';
import { WatchDutyThemeProvider } from 'theme';
import { RestoredPhotoListener } from 'components/RestoredPhotoListener/RestoredPhotoListener';
import {
  initPushNotifications,
  setCacheBusterTsFromNotifications,
} from '../shared/initPushNotifications';
import { ErrorFallback } from '../components/ErrorFallback';
import { CacheableQueryClientProvider } from '../components/CacheableQueryClientProvider';
import { useForegroundState } from '../hooks/useForegroundState';
import { usePrevious } from '../hooks/usePrevious';
import { Navigation } from './Navigation';
import { PostHogProviderWrapper } from '../libs/posthog/PostHogProviderWrapper';
import { ThemeInitializer } from '../components/ThemeInitializer';

const PUSH_REREGISTER_FREQUENCY_MINUTES = 60;

const DeeplinkListener = (): null => {
  const myHistory = useHistory();
  const clearMapSelectionsForRouterEntity =
    useClearMapSelectionsForRouterEntity();
  const { setCacheState } = useCacheState();

  useCapacitorListener('appUrlOpen', (event) => {
    // Example url: https://app.watchduty.org/incident/1 > /incident/1
    const slug = event.url.split('watchduty.org').pop();
    if (slug) {
      // Deep links can target an incident that isn't in the cached geo events list (e.g. a new incident, or
      // one from a county they aren't subscribed).So bust it here like we do with push notifications
      setCacheState(Math.round(Date.now() / 1000) * 1000);
      clearMapSelectionsForRouterEntity();
      myHistory.push(slug);
    }
  });

  return null;
};

const PushInitializer = (): null => {
  const myHistory = useHistory();
  const clearMapSelectionsForRouterEntity =
    useClearMapSelectionsForRouterEntity();
  const { setCacheState } = useCacheState();
  const { isPushEnabled } = useArePushNotificationsEnabled();
  const { onboardCompleted } = useOnboardingState();
  const deviceInfo = getDeviceInfo();
  const appState = useForegroundState();
  const prevAppStateActive = usePrevious(appState.isActive);
  const lastRunRef = useRef<Date>();
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!isNative) return;
    // background OR cold-start -> foreground
    //   in the case of a cold-start, the async code running in setCacheBusterTsFromNotifications
    //   may not run before we start with queries, but it will have set the cacheBuster for subsequent queries
    if (!prevAppStateActive && appState.isActive) {
      setCacheBusterTsFromNotifications(setCacheState);
    }
  }, [prevAppStateActive, appState.isActive, setCacheState, isNative]);

  // this only needs to run once based on isPushEnabled every time the app is loaded
  // If the user hasn't given us permission yet, request this during the onboarding flow
  // without this useEffect, this is run every time the route changes.
  const platform = deviceInfo?.platform;
  useEffect(() => {
    if (!isPushEnabled) return;
    if (platform === 'android' && !onboardCompleted) {
      /**
       * Notifications are enabled by default on Android < 13, so we have to wait for the
       * onboarding flow to complete, if not we'll see a location permission request
       * as soon as we open the app (we get the user's position as part of the push
       * token registration). This does not causes issues with notifications because
       * the onboarding flow also inits and register a push token.
       */
      return;
    }

    // Re-run registration on app resume, limited to once an hour. initPushNotifications()
    // won't re-send the token to Watch Duty or localStorage if it's the same
    if (
      !appState.isActive ||
      (lastRunRef.current &&
        differenceInMinutes(new Date(), lastRunRef.current) <
          PUSH_REREGISTER_FREQUENCY_MINUTES)
    ) {
      return;
    }

    // cause re-initializing the push registrations
    initPushNotifications(
      myHistory,
      setCacheState,
      undefined,
      clearMapSelectionsForRouterEntity,
    ).then(() => {
      lastRunRef.current = new Date();
    });
  }, [
    appState.isActive,
    isPushEnabled,
    platform,
    onboardCompleted,
    myHistory,
    setCacheState,
    clearMapSelectionsForRouterEntity,
  ]);

  return null;
};

const SentryUserInitializer = (): null => {
  const { user } = useAuthState();
  if (user) {
    Sentry.setTag('user_id', user.id);
  }
  return null;
};

export const App = (): JSX.Element => (
  <SentryReact.ErrorBoundary fallback={ErrorFallback}>
    <CacheableQueryClientProvider>
      <RecoilRoot>
        <PostHogProviderWrapper>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Router history={history}>
              <PushInitializer />
              <SentryUserInitializer />
              <DeeplinkListener />
              <RestoredPhotoListener />
              <StyledEngineProvider injectFirst>
                <WatchDutyThemeProvider defaultMode="system" noSsr>
                  <ThemeInitializer />
                  <ConfirmProvider>
                    <KeyboardShortcutsDialog />
                    <CommandPaletteDialog />
                    <WatchDutySnackbarProvider />
                    <CssBaseline />
                    <Navigation />
                  </ConfirmProvider>
                </WatchDutyThemeProvider>
              </StyledEngineProvider>
            </Router>
          </LocalizationProvider>
        </PostHogProviderWrapper>
      </RecoilRoot>
    </CacheableQueryClientProvider>
  </SentryReact.ErrorBoundary>
);
